import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { WORK, WORK_GROUPS } from '../../site/work';

// The sites we built, shown moving: a short looping recording of each live
// site inside a phone on small screens (a swipe carousel) and inside a
// laptop with a phone beside it on large screens (a stage with a list).
// Dark, with the active site's own colour glowing behind it. Recordings
// play only while on screen, and never when the visitor prefers reduced
// motion (the poster frame shows instead).

const TABS = [{ id: 'all', short: 'All' }, ...WORK_GROUPS];
const STAGE_SECONDS = 14;

export default function SiteShowcase() {
  const [groupId, setGroupId] = useState('all');
  const sites = useMemo(() => (groupId === 'all' ? WORK : WORK.filter((w) => w.group === groupId)), [groupId]);
  const [index, setIndex] = useState(0);
  const reduced = usePrefersReducedMotion();
  const safeIndex = Math.min(index, sites.length - 1);
  const active = sites[safeIndex];
  const pick = (id) => { setGroupId(id); setIndex(0); };

  return (
    <section id="work" className="relative overflow-hidden bg-[#0a0b10] text-white py-14 md:py-24">
      <style>{`
        .showcase-track { scrollbar-width: none; -ms-overflow-style: none; }
        .showcase-track::-webkit-scrollbar { display: none; }
        @keyframes showcaseProgress { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        @keyframes showcaseIn { from { opacity: 0; transform: translateY(10px) scale(0.985); } to { opacity: 1; transform: none; } }
        .showcase-in { animation: showcaseIn 700ms cubic-bezier(0.2, 0.7, 0.2, 1) both; }
        @media (prefers-reduced-motion: reduce) { .showcase-in { animation: none; } }
      `}</style>
      <Glow accent={active.accent} />

      <div className="relative container mx-auto max-w-6xl px-4">
        <div className="text-center mb-7 md:mb-10">
          <p className="text-white/50 font-semibold uppercase tracking-[0.28em] text-[10px] md:text-xs mb-3 md:mb-4">Sites we built</p>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight leading-[1.05]">See them move.</h2>
          <p className="text-white/55 max-w-xl mx-auto mt-3 md:mt-4 text-sm md:text-base">Every one is live. These are the real sites. Swipe, then open one on your phone and try to order, book, or call.</p>
        </div>

        <div className="flex justify-center mb-8 md:mb-12">
          <div role="tablist" aria-label="Kind of business" className="showcase-track flex gap-1.5 p-1 rounded-full bg-white/[0.06] border border-white/10 overflow-x-auto max-w-full">
            {TABS.map((t) => {
              const on = t.id === groupId;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => pick(t.id)}
                  className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${on ? 'bg-white text-slate-900' : 'text-white/70 hover:text-white'}`}
                >
                  {t.short}
                </button>
              );
            })}
          </div>
        </div>

        <div className="lg:hidden">
          <PhoneCarousel key={groupId} sites={sites} index={safeIndex} setIndex={setIndex} reduced={reduced} />
        </div>
        <div className="hidden lg:block">
          <DesktopStage sites={sites} index={safeIndex} setIndex={setIndex} reduced={reduced} />
        </div>
      </div>
    </section>
  );
}

// Two soft blobs in the active site's colour. Colour changes fade.
function Glow({ accent }) {
  return (
    <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
      <div className="absolute left-1/2 top-[28%] -translate-x-1/2 w-[70vw] max-w-[820px] aspect-square rounded-full blur-3xl opacity-[0.28] transition-colors duration-1000" style={{ backgroundColor: accent }} />
      <div className="absolute right-[-10%] bottom-[-10%] w-[40vw] max-w-[520px] aspect-square rounded-full blur-3xl opacity-[0.16] transition-colors duration-1000" style={{ backgroundColor: accent }} />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,#0a0b10_90%)]" />
    </div>
  );
}

// Small screens: one phone per slide, snap scrolling, the visible slide
// plays. The track runs edge to edge (index.css caps divs at 100% below
// 1024px, so it opts out).
function PhoneCarousel({ sites, index, setIndex, reduced }) {
  const trackRef = useRef(null);
  useEffect(() => {
    const track = trackRef.current;
    if (!track || typeof IntersectionObserver === 'undefined') return undefined;
    const slides = [...track.querySelectorAll('[data-slide]')];
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) setIndex(Number(e.target.dataset.slide));
    }, { root: track, threshold: 0.6 });
    slides.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, [sites, setIndex]);

  const jump = (i) => {
    const slide = trackRef.current?.querySelector(`[data-slide="${i}"]`);
    slide?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  };

  return (
    <div>
      <div ref={trackRef} className="showcase-track flex gap-5 overflow-x-auto snap-x snap-mandatory -mx-4 px-[7vw] pb-2 max-w-none w-[calc(100%+2rem)]">
        {sites.map((w, i) => (
          <div key={w.slug} data-slide={i} className="shrink-0 w-[80vw] max-w-[380px] snap-center">
            <PhoneFrame site={w} playing={i === index} reduced={reduced} />
            <Caption site={w} centered />
          </div>
        ))}
      </div>
      <div className="flex justify-center gap-2 mt-4" aria-hidden="true">
        {sites.map((w, i) => (
          <button key={w.slug} type="button" tabIndex={-1} onClick={() => jump(i)} className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? 'w-6 bg-white' : 'w-1.5 bg-white/30'}`} />
        ))}
      </div>
    </div>
  );
}

// Large screens: a list on the left, the active site in a laptop and a
// phone on the right. Advances on its own; pauses while the pointer is on
// the stage.
function DesktopStage({ sites, index, setIndex, reduced }) {
  const [hover, setHover] = useState(false);
  const active = sites[index];
  useEffect(() => {
    if (hover || reduced || sites.length < 2) return undefined;
    const t = setTimeout(() => setIndex((index + 1) % sites.length), STAGE_SECONDS * 1000);
    return () => clearTimeout(t);
  }, [index, hover, reduced, sites, setIndex]);

  return (
    <div className="grid grid-cols-[280px_1fr] gap-10 xl:gap-14 items-center" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <ol className="space-y-1">
        {sites.map((w, i) => {
          const on = i === index;
          return (
            <li key={w.slug}>
              <button
                type="button"
                onClick={() => setIndex(i)}
                aria-current={on ? 'true' : undefined}
                className={`relative w-full text-left rounded-xl px-4 py-3 transition-colors ${on ? 'bg-white/[0.08]' : 'hover:bg-white/[0.04]'}`}
              >
                <span className="flex items-center gap-3">
                  <span className="shrink-0 w-2 h-2 rounded-full" style={{ backgroundColor: w.accent }} />
                  <span className="min-w-0">
                    <span className={`block text-sm font-semibold truncate ${on ? 'text-white' : 'text-white/75'}`}>{w.name}</span>
                    <span className="block text-xs text-white/45 truncate">{w.kind} · {w.place}</span>
                  </span>
                </span>
                {on && !hover && !reduced && sites.length > 1 && (
                  <span className="absolute left-4 right-4 bottom-1 h-px bg-white/15 overflow-hidden" aria-hidden="true">
                    <span key={w.slug} className="block h-full bg-white/70 origin-left" style={{ animation: `showcaseProgress ${STAGE_SECONDS}s linear both` }} />
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ol>

      <div>
        <div className="relative pr-10 xl:pr-12">
          <div key={active.slug} className="showcase-in">
            <LaptopFrame site={active} reduced={reduced} />
          </div>
          <div key={`${active.slug}-phone`} className="showcase-in absolute -bottom-16 right-0 w-[200px] xl:w-[220px]" style={{ animationDelay: '120ms' }}>
            <PhoneFrame site={active} playing reduced={reduced} small />
          </div>
        </div>
        <Caption site={active} className="mt-8 pr-[250px] xl:pr-[270px]" />
      </div>
    </div>
  );
}

function Caption({ site, centered = false, className = '' }) {
  return (
    <div className={`${centered ? 'text-center' : ''} ${className}`}>
      <p className="text-white/45 text-[10px] md:text-xs uppercase tracking-[0.22em] mt-5 mb-1">{site.kind} · {site.place}</p>
      <h3 className="text-xl md:text-2xl font-bold tracking-tight">{site.name}</h3>
      <p className="text-white/60 text-sm leading-relaxed mt-1.5">{site.line}</p>
      <div className={`flex flex-wrap items-center gap-2 mt-4 ${centered ? 'justify-center' : ''}`}>
        {site.result && (
          <span className="inline-flex items-center rounded-full px-3 py-1.5 text-xs font-semibold text-slate-900" style={{ backgroundColor: site.accent }}>{site.result}</span>
        )}
        <a href={site.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-white text-slate-900 px-4 py-2 text-sm font-semibold hover:bg-white/90 transition-colors">
          Open {site.domain}
          <ArrowUpRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
}

// A phone, drawn in CSS, with the recording inside it.
export function PhoneFrame({ site, playing, reduced, small = false }) {
  return (
    <div className={`relative mx-auto ${small ? 'w-full' : 'w-[min(70vw,300px)]'}`}>
      <div className="rounded-[2.9rem] bg-[#15161c] p-[8px] shadow-[0_40px_90px_-24px_rgba(0,0,0,0.85),inset_0_0_0_1px_rgba(255,255,255,0.08)] transition-shadow duration-1000" style={{ boxShadow: `0 40px 90px -24px rgba(0,0,0,0.85), 0 0 ${small ? 90 : 140}px -30px ${site.accent}, inset 0 0 0 1px rgba(255,255,255,0.08)` }}>
        <div className="relative aspect-[9/19.5] overflow-hidden rounded-[2.4rem] bg-black">
          <SiteVideo src={site.video.phone} poster={site.poster.phone} playing={playing} reduced={reduced} label={`${site.name} on a phone`} />
          <div className="absolute top-[10px] left-1/2 -translate-x-1/2 h-[24px] w-[84px] rounded-full bg-black" aria-hidden="true" />
          <div className="absolute inset-0 rounded-[2.4rem] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)] pointer-events-none" aria-hidden="true" />
        </div>
      </div>
      <span className="absolute -left-[2px] top-[18%] h-[7%] w-[3px] rounded-l bg-[#2a2b33]" aria-hidden="true" />
      <span className="absolute -left-[2px] top-[28%] h-[7%] w-[3px] rounded-l bg-[#2a2b33]" aria-hidden="true" />
      <span className="absolute -right-[2px] top-[22%] h-[10%] w-[3px] rounded-r bg-[#2a2b33]" aria-hidden="true" />
    </div>
  );
}

// A browser window with the desktop recording inside it.
function LaptopFrame({ site, reduced }) {
  return (
    <div className="rounded-2xl bg-[#15161c] p-2 shadow-[0_50px_120px_-30px_rgba(0,0,0,0.9),inset_0_0_0_1px_rgba(255,255,255,0.08)]">
      <div className="flex items-center gap-3 px-3 py-2">
        <span className="flex gap-1.5" aria-hidden="true">
          <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
        </span>
        <span className="flex-1 text-center text-xs text-white/50 bg-white/[0.06] rounded-md py-1 truncate">{site.domain}</span>
        <span className="w-10" aria-hidden="true" />
      </div>
      <div className="relative aspect-[16/10] overflow-hidden rounded-xl bg-black">
        <SiteVideo src={site.video.desktop} poster={site.poster.desktop} playing reduced={reduced} label={`${site.name} on a desktop`} />
      </div>
    </div>
  );
}

// The recording itself. Plays only when asked to and when motion is fine;
// otherwise the poster shows.
function SiteVideo({ src, poster, playing, reduced, label }) {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (playing && !reduced) v.play().catch(() => {});
    else v.pause();
  }, [playing, reduced, src]);
  return (
    <video
      ref={ref}
      src={src}
      poster={poster}
      muted
      playsInline
      loop
      preload={playing ? 'auto' : 'none'}
      aria-label={label}
      className="absolute inset-0 w-full h-full object-cover object-top"
    />
  );
}

export function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);
  return reduced;
}
